import { Badge, type Tone } from "./badge.tsx";

/**
 * Status colours in one place, so "invalidated" is the same red everywhere
 * rather than whatever each page's author reached for.
 *
 * The five tones are a ladder, and a value sits on it by what it asks of the
 * person reading it rather than by which enum it arrived in — which is why one
 * table can serve a menu row, a printer and a settlement at once:
 *
 * - **neutral** — nothing has happened yet and nothing is owed: a draft, a
 *   queued job, a printer nobody has paired, a menu row its merchant has
 *   hidden. Deliberately off is not a failure, and it reads here rather than
 *   in **danger** for that reason: the ladder is about what a state asks of
 *   the person reading it, and a hidden item asks nothing.
 * - **primary** — under way, needing nothing: a trial, a stock count being
 *   taken, a ticket on the printer.
 * - **success** — the good end of the machine: active, received, paid.
 * - **warning** — a person has to do something: an unpaid bill, a document
 *   waiting to be reviewed, a printer that has dropped off the network.
 * - **danger** — it failed, or it was undone: rejected, voided, cancelled.
 *
 * A table's four occupancy states read as an escalation towards "a member of
 * staff is needed" — free, then seated, then in service and wanting nothing,
 * then waiting to pay — and they are here rather than on the till because the
 * till and the back office are looking at the same four tables. The till kept
 * its own four colours until this table covered them, and disagreed on two.
 *
 * A value this table does not hold renders grey, and grey reads as "nothing to
 * report" for a state that may be anything but: a hidden menu item and an
 * active one were the same grey until the table covered both, and a rejected
 * outbound document was green. Pages papered over the gaps by passing a status
 * they did not have — `printed` went in as `completed` to buy the colour —
 * which is how a second, private table starts. `status-tones.test.ts` fails
 * when a badge can show a word this one does not have.
 */
export const STATUS_TONES: Record<string, Tone> = {
  // Nothing has happened to it yet, or nothing is left to do about it.
  draft: "neutral",
  // "Hidden" on a menu row, "Disabled" on a staff account — both a switch
  // somebody threw on purpose. This was `danger` until 2026-09-01, which put a
  // hidden category in the same red as a rejected document and read as an
  // error on a screen where nothing was wrong.
  disabled: "neutral",
  queued: "neutral",
  unbound: "neutral",
  closed: "neutral",
  refunded: "neutral",
  ordered: "neutral",

  // Under way. Somebody or something already has it.
  trialing: "primary",
  new: "primary",
  submitted: "primary",
  purchasing: "primary",
  approving: "primary",
  transferring: "primary",
  counting: "primary",
  printing: "primary",
  running: "primary",
  seated: "primary",

  // It ended well.
  active: "success",
  enabled: "success",
  normal: "success",
  completed: "success",
  converted: "success",
  ended: "success",
  received: "success",
  issued: "success",
  adjusted: "success",
  settled: "success",
  ready: "success",
  printed: "success",
  paid: "success",
  online: "success",
  free: "success",

  // Waiting on a person.
  pending: "warning",
  pending_audit: "warning",
  pending_inbound: "warning",
  waiting_review: "warning",
  head_office_reviewing: "warning",
  contacted: "warning",
  suspended: "warning",
  expired: "warning",
  unpaid: "warning",
  partly_paid: "warning",
  credit: "warning",
  offline: "warning",
  bill_requested: "warning",

  // It failed, or somebody undid it.
  invalidated: "danger",
  cancelled: "danger",
  failed: "danger",
  rejected: "danger",
  head_office_rejected: "danger",
  voided: "danger",
  blocked: "danger",
  error: "danger",
};

export function StatusBadge({
  status,
  label,
  dot = true,
}: {
  status: string;
  label?: string;
  dot?: boolean;
}) {
  return (
    <Badge tone={STATUS_TONES[status] ?? "neutral"} dot={dot}>
      {label ?? status.replace(/_/g, " ")}
    </Badge>
  );
}
